import { Component, OnInit } from '@angular/core';
import { Misc } from '../util/misc';
import { UntypedFormGroup, UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { debounceTime, startWith, map } from 'rxjs/operators';
import { Subject, Observable, forkJoin } from 'rxjs';
import { ToastrComponent } from '../util/toastr/toastr.component';
import { MatDialogRef } from '@angular/material/dialog';
import { Cliente } from 'src/app/models/cliente.model';
import { Projeto } from 'src/app/models/projeto.model';
import { EstimativaTipo } from 'src/app/models/estimativa-tipo.model';
import { Estimativa } from 'src/app/models/estimativa.model';
import { ClienteService } from 'src/app/services/cliente.service';
import { ProjetoService } from 'src/app/services/projeto.service';
import { EstimativaService } from 'src/app/services/estimativa.service';
import { EstimativaTipoService } from 'src/app/services/estimativa-tipo.service';
import { UtilService } from 'src/app/services/util.service';
import { VwCrmModel } from 'src/app/models/vw.crm.model';
import { CrmService } from 'src/app/services/crm.service';
import { Router } from '@angular/router';
import * as constants from 'src/app/models/constants/constants';
import { ViewChild } from '@angular/core';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';

@Component({
  selector: 'app-estimativa-nova',
  templateUrl: './estimativa-nova.component.html',
  styleUrls: ['./estimativa-nova.component.scss'],
})
export class EstimativaNovaComponent implements OnInit {
  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger!: MatAutocompleteTrigger;

  public keyUp = new Subject<MouseEvent>();

  loading = false;

  clientes: Array<Cliente> = [];
  clientesFiltrada: Observable<Array<Cliente>>;

  projetos: Array<Projeto> = [];
  vwClienteCrm: VwCrmModel[];

  estimativaTipos: Array<EstimativaTipo> = [];

  novoProjeto = false;
  crmPrefix = 'P' + new Date().getFullYear();

  quotePartial: any;
  nomeOportuinidadeParcial: any;

  crmId = null;
  accountId: any;

  filtroCliente: UntypedFormControl = new UntypedFormControl();
  basicForm: UntypedFormGroup;

  filtroProposta: UntypedFormControl = new UntypedFormControl();
  propostasFiltradas: Observable<any[]>;

  valoresRepetidos = [];
  doubleDataSource = [];
  dadosDesordenado = [];
  dadosEmUso = [];

  constructor(
    public misc: Misc,
    private fb: UntypedFormBuilder,
    private clienteService: ClienteService,
    private projetoService: ProjetoService,
    private crmService: CrmService,
    private estimativaService: EstimativaService,
    private estimativaTipoService: EstimativaTipoService,
    private toastr: ToastrComponent,
    private utils: UtilService,
    private router: Router,
    private dialogRef: MatDialogRef<EstimativaNovaComponent>,
  ) {
    this.createForm();

    this.keyUp.pipe(debounceTime(250)).subscribe(() => {
      this.enterSave();
    });
  }

  ngOnInit() {
    this.loading = true;

    const subscriptions: any = {};

    const isAdmin = this.utils.isAdmin();
    const unidadeId = this.utils.getUnidadeId();

    // REGRA: admin carrega tudo, senão carrega por unidade
    if (isAdmin) {
      subscriptions.clientes = this.clienteService.GetAllIncludeNotActive();
    } else {
      subscriptions.clientes = this.clienteService.getAllByUnidade(unidadeId);
    }

    forkJoin(subscriptions)
      .subscribe({
        next: (result: any) => {
          const raw = result?.clientes;

          const items: Cliente[] = Array.isArray(raw) ? raw : (raw?.items ?? []);

          this.clientes = items;

          this.clientesFiltrada = this.filtroCliente.valueChanges.pipe(
            startWith(''),
            map((value) => this._filterClientes(value)),
          );

          // força emitir
          this.filtroCliente.setValue(this.filtroCliente.value ?? '', { emitEvent: true });
        },
        error: (error) => {
          this.misc.processError(error, 'Não foi possível carregar os dados da tela.');
        },
      })
      .add(() => {
        this.loading = false;
      });

    this.getEstimativaTipos();

    this.propostasFiltradas = this.filtroProposta.valueChanges.pipe(
      startWith(''),
      map((value) => this._filterPropostas(value)),
    );
  }

  onFocusCliente() {
    this.filtroCliente.setValue(this.filtroCliente.value ?? '', { emitEvent: true });

    // abre o painel mesmo sem digitar
    if (this.clientes?.length) {
      this.autocompleteTrigger.openPanel();
    }
  }

  getEstimativaTipos() {
    this.estimativaTipoService.getAll().subscribe((result: any) => {
      const items = result?.items ?? [];

      this.estimativaTipos = items.filter(
        (t: any) => t.id !== constants.TipoEstimativaConstants.ID_TIPO_ESTIMATIVA_DELIVERY,
      );
    });
  }

  createForm() {
    this.basicForm = this.fb.group({
      cliente: [null],
      projeto: [{ value: null, disabled: true }],
      novoProjeto: [{ value: '', disabled: true }],
      crm: [{ value: '0000', disabled: true }],
      estimativa: [{ value: '', disabled: true }],
      estimativaTipo: [{ value: null, disabled: true }],
    });
  }

  // robusto: sempre retorna array
  private _filterClientes(value: any): Cliente[] {
    const termo = typeof value === 'string' ? value : (value?.nome ?? '');
    const filterValue = (termo || '').toLowerCase();

    // se estiver digitando, limpa estado do form
    if (typeof value === 'string') {
      this.limpar();
      this.novoProjeto = false;
    }

    if (!filterValue) return this.clientes ?? [];
    return (this.clientes ?? []).filter((c) => (c.nome || '').toLowerCase().includes(filterValue));
  }

  displayFn(cliente: Cliente): string {
    return cliente && cliente.nome ? cliente.nome : '';
  }

  onClienteSelecionado(cliente: Cliente) {
    this.basicForm.get('cliente')?.setValue(cliente);
    this.filtroCliente.setValue(cliente, { emitEvent: false });
    this.carregaProjetos(cliente);
  }

  onPropostaSelecionada(proj: any) {
    this.basicForm.get('projeto')?.enable();
    this.basicForm.get('projeto')?.setValue(proj);

    const quote = proj?.quote_No ?? proj?.crmId ?? '';
    this.quotePartial = quote || this.quotePartial;

    this.basicForm.get('crm')?.enable();
    this.basicForm.get('crm')?.setValue(quote || '0000');
  }

  limpar() {
    this.basicForm.patchValue({
      projeto: null,
      novoProjeto: '',
      crm: '0000',
      estimativa: '',
      estimativaTipo: null,
    });
  }

  adicionarProjeto() {
    this.novoProjeto = !this.novoProjeto;
    this.basicForm.get('projeto')?.enable();
    this.loading = true;
    this.limpar();

    this.crmService
      .getClientesCrm(this.accountId)
      .subscribe({
        next: (cotacoes) => {
          this.vwClienteCrm = cotacoes ?? [];

          const clienteCRMFiltrado = this.vwClienteCrm.map((vw) => vw);
          const projetoFiltrado = (this.projetos || []).map((proj) => proj);

          this.valoresRepetidos = clienteCRMFiltrado.filter((c) => projetoFiltrado.find((p) => p.crmId == c.quote_No));

          this.doubleDataSource = [];
          clienteCRMFiltrado.forEach((item) => {
            if (!this.valoresRepetidos.find((b) => b.quote_No == item.quote_No)) {
              this.doubleDataSource.push(item);
            }
          });

          this.dadosDesordenado = this.doubleDataSource.concat(projetoFiltrado);

          const itensOrdemUnificado: string[] = [];

          this.dadosDesordenado.forEach((ele) => {
            if (ele.quote_No) itensOrdemUnificado.push(ele.quote_No.replace('P', ''));
            if (ele.crmId) itensOrdemUnificado.push(ele.crmId.replace('P', ''));
          });

          itensOrdemUnificado.sort().reverse();

          this.dadosEmUso = [];

          itensOrdemUnificado.forEach((item) => {
            this.dadosDesordenado.filter((ele) => {
              const elemento = ele.quote_No ? ele.quote_No.replace('P', '') : ele.crmId.replace('P', '');

              if (elemento == item) {
                if (ele.nome_Oportunidade === null) ele.nome_Oportunidade = ele.assunto;

                const existeNaLista = this.dadosEmUso.find((x) =>
                  x.quote_No ? x.quote_No === ele.quote_No : x.crmId === ele.crmId,
                );

                if (!existeNaLista) this.dadosEmUso.push(ele);
              }
            });
          });

          this.filtroProposta.setValue(this.filtroProposta.value ?? '', { emitEvent: true });
        },
        error: (error) => {
          this.misc.processError(error, 'Não foi possível carregar a lista de projetos.');
        },
      })
      .add(() => {
        this.loading = false;
      });

    this.basicForm.get('novoProjeto')?.enable();
    this.basicForm.get('estimativa')?.enable();
    this.basicForm.get('estimativaTipo')?.enable();
  }

  carregaProjetos(cliente: Cliente) {
    this.limparDadosCombos();

    this.novoProjeto = false;
    this.crmId = cliente.crmId;
    this.accountId = cliente.accountId;

    this.limpar();
    this.basicForm.patchValue({ cliente });

    if (cliente) {
      this.loading = true;

      this.projetoService
        .getByClienteId(cliente.id)
        .subscribe({
          next: (projetos) => {
            this.projetos = projetos ?? [];
            this.basicForm.get('projeto')?.enable();
            this.basicForm.get('novoProjeto')?.enable();
            this.basicForm.get('crm')?.enable();
            this.adicionarProjeto();
          },
          error: (error) => {
            this.misc.processError(error, 'Não foi possível carregar a lista de projetos.');
          },
        })
        .add(() => {
          this.loading = false;
        });
    }

    this.filtroProposta.setValue('');
  }

  enterSave() {
    if (!this.basicForm.valid) return;
    this.save();
  }

  save() {
    this.basicForm.get('crm')?.setValue(this.quotePartial);
    if (!this.validaFormulario()) return;

    this.loading = true;

    const form = this.basicForm.getRawValue();
    const projetoSel = form.projeto;
    const isNovoViaCRM = !!projetoSel?.quote_No;
    const projetoIdExistente = projetoSel?.id ?? 0;

    const estimativa: any = {
      id: 0,
      nome: form.estimativa,
      estimativaTipoId: form.estimativaTipo.id,
      projetoId: isNovoViaCRM ? 0 : projetoIdExistente,
      usuarioId: this.utils.getUserId(),
      negocioId: 1,
      pracaId: 1,
      statusId: 1,
      unidadeId: 1,
    };

    if (isNovoViaCRM) {
      const projetoNovo: Projeto = {
        id: 0,
        clienteId: form.cliente.id,
        crmId: projetoSel.quote_No,
        ano: new Date().getFullYear().toString(),
        nome: projetoSel.nome_Oportunidade ?? projetoSel.assunto ?? '',
        usuarioId: this.utils.getUserId(),
        tbEstimativa: [estimativa],
      } as any;

      this.projetoService.create(projetoNovo).subscribe({
        next: (result) => {
          estimativa.projetoId = result.id;
          this.criaEstimativa(estimativa);
        },
        error: (error) => {
          this.misc.processError(error);
          this.toastr.mostrarMensagem(
            'erro',
            'Erro',
            'Ocorreu um erro ao criar o projeto da estimativa, contate o administrador.',
          );
          this.loading = false;
        },
      });
    } else {
      this.criaEstimativa(estimativa);
    }
  }

  criaEstimativa(estimativa: any) {
    this.estimativaService.create(estimativa as Estimativa).subscribe({
      next: (result) => {
        const estimativaId = result.id;
        this.router.navigate(['/price/projeto/estimativa-edit/' + estimativaId]);
        this.dialogRef.close(estimativaId);
      },
      error: (error) => {
        if (error.status === 400) {
          this.toastr.mostrarMensagem(
            'erro',
            'Erro',
            'Inclusão não permitida. Nome de estimativa já criada para esta proposta.',
          );
          this.loading = false;
        } else {
          this.misc.processError(error);
          this.toastr.mostrarMensagem(
            'erro',
            'Erro',
            'Ocorreu um erro ao criar a estimativa, contate o administrador.',
          );
          this.loading = false;
        }
      },
    });
  }

  validaFormulario() {
    const mensagens: string[] = [];
    const form = this.basicForm.getRawValue();
    const projeto = form.projeto;

    if (!form.cliente) mensagens.push('Selecione um cliente!');

    if (!projeto) {
      mensagens.push('Selecione o projeto da estimativa!');
    } else {
      const quote = projeto?.quote_No ?? projeto?.crmId ?? form.crm ?? '';
      const isNovoViaCRM = !!projeto?.quote_No;

      if (!quote) {
        mensagens.push('Informe o CRM do novo projeto!');
      } else if (isNovoViaCRM) {
        if (quote.length !== 9 || quote === '0000') {
          this.toastr.mostrarMensagem('aviso', 'Atenção', 'Informe o CRM no formato ' + this.crmPrefix + '0000');
          return false;
        }
      }
    }

    if (!form.estimativa) mensagens.push('Informe a descrição da estimativa!');
    if (!form.estimativaTipo) mensagens.push('Selecione o tipo da estimativa!');

    if (mensagens.length) {
      this.toastr.mostrarMensagem('aviso', 'Atenção!', mensagens.join('<br />'));
      return false;
    }
    return true;
  }

  onEnableInputs(vw: VwCrmModel) {
    this.quotePartial = vw.quote_No.toString();
    this.nomeOportuinidadeParcial = vw.nome_Oportunidade;
  }

  close(): void {
    this.dialogRef.close();
  }

  limparDadosCombos() {
    if (this.projetos?.length) this.projetos = [];
    if (this.dadosEmUso?.length) this.dadosEmUso = [];
    if (this.dadosDesordenado?.length) this.dadosDesordenado = [];
    if (this.valoresRepetidos?.length) this.valoresRepetidos = [];
    if (this.doubleDataSource?.length) this.doubleDataSource = [];
  }

  get dadosEmUsoFiltrados(): any[] {
    return this.dadosEmUso.filter((p) => !['Cancelada', 'Erro', 'Rejected'].includes(p.estagio));
  }

  onFocusProposta() {
    if (!this.filtroProposta.value) this.filtroProposta.setValue('');
  }

  private _filterPropostas(value: any): any[] {
    const filterValue = typeof value === 'string' ? value.toLowerCase() : '';

    return this.dadosEmUsoFiltrados.filter((p) => {
      const termo = (
        (p.crmId || p.quote_No || '') +
        ' ' +
        (p.nome || p.nome_Oportunidade || p.assunto || '')
      ).toLowerCase();
      return termo.includes(filterValue);
    });
  }

  displayPropostaFn(projeto: any): string {
    if (!projeto) return '';
    return `${projeto.crmId || projeto.quote_No || ''} | ${projeto.nome || projeto.nome_Oportunidade || projeto.assunto || ''}`;
  }
}
